import React from "react";
import ReactDOM from "react-dom";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";

type Appearance = "success" | "warning" | "error" | "info";

const DEFAULT_DURATION_MS = 3000;

/**
 * Drop-in replacement for antd's `message` API, rendered with MDS
 * (McToast + McNotification) instead of antd. Call sites keep the same
 * signature: `message.success("text")` / `message.error("text", 8)`.
 *
 * Each call mounts a throwaway container on document.body, shows the toast,
 * and unmounts it after the duration elapses (antd's duration is in seconds;
 * mc-toast expects milliseconds).
 */
const show = (appearance: Appearance) => (content: React.ReactNode, durationSec?: number) => {
  if (typeof document === "undefined") return;

  const container = document.createElement("div");
  document.body.appendChild(container);

  const durationMs =
    typeof durationSec === "number" && durationSec > 0
      ? durationSec * 1000
      : DEFAULT_DURATION_MS;

  const unmount = () => {
    ReactDOM.unmountComponentAtNode(container);
    container.remove();
  };

  ReactDOM.render(
    <McToast appearance={appearance} open duration={durationMs} close={unmount}>
      <McNotification body={typeof content === "string" ? content : undefined}>
        {typeof content === "string" ? undefined : content}
      </McNotification>
    </McToast>,
    container
  );

  window.setTimeout(unmount, durationMs + 300);
};

export const message = {
  success: show("success"),
  warning: show("warning"),
  error: show("error"),
  info: show("info"),
};
